import React, { useEffect, useState, useCallback } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Notiflix from "notiflix";

const API_BASE = process.env.REACT_APP_MEMBERSHIP_API || "http://localhost:5001";

const statusBadge = {
  open: { label: "Open", cls: "bg-blue-100 text-blue-800" },
  in_progress: { label: "In Progress", cls: "bg-purple-100 text-purple-800" },
  waiting_member: { label: "Waiting on Member", cls: "bg-amber-100 text-amber-800" },
  resolved: { label: "Resolved", cls: "bg-green-100 text-green-800" },
  closed: { label: "Closed", cls: "bg-gray-200 text-gray-600" },
};
const categoryLabel = {
  general: "General",
  remote: "🖥️ Remote",
  hardware: "🎁 Hardware",
  digital: "📈 Growth",
  lamna: "🦈 LAMNA",
  volunteer: "🤝 Volunteer App",
};

const fmtDate = (d) => new Date(d).toLocaleDateString();

const StaffWorkspace = () => {
  useDocTitle("ZelT | Staff Workspace");
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ status: "", category: "", assigned: "", search: "" });
  const [sessionLog, setSessionLog] = useState("");
  const [detail, setDetail] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [tab, setTab] = useState("board"); // board | team
  const [staffList, setStaffList] = useState(null);
  const [staffEmail, setStaffEmail] = useState("");
  const [staffRole, setStaffRole] = useState("staff");

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("zelt_token")}` });

  useEffect(() => {
    // determine role; members are bounced to member view
    axios
      .get(`${API_BASE}/my-role`, { headers: headers() })
      .then((r) => {
        if (r.data.role === "member") {
          navigate("/members");
          return;
        }
        setRole(r.data.role);
      })
      .catch(() => navigate("/membership"));
  }, [navigate]);

  const loadTickets = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.category) params.set("category", filters.category);
    if (filters.assigned) params.set("assigned", filters.assigned);
    if (filters.search) params.set("search", filters.search);
    axios
      .get(`${API_BASE}/staff/tickets?${params}`, { headers: headers() })
      .then((r) => setTickets(r.data.tickets))
      .catch(() => {});
  }, [filters]);

  const loadStats = useCallback(() => {
    axios
      .get(`${API_BASE}/staff/stats`, { headers: headers() })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  const loadStaff = useCallback(() => {
    if (role !== "admin") return;
    axios
      .get(`${API_BASE}/admin/staff`, { headers: headers() })
      .then((r) => setStaffList(r.data.staff))
      .catch(() => {});
  }, [role]);

  useEffect(() => {
    if (role) {
      loadTickets();
      loadStats();
      loadStaff();
    }
  }, [role, loadTickets, loadStats, loadStaff]);

  const openTicket = (id) => {
    axios
      .get(`${API_BASE}/staff/tickets/${id}`, { headers: headers() })
      .then((r) => {
        setDetail(r.data);
        setReplyText("");
        setIsInternal(false);
      })
      .catch(() => {});
  };

  const sendReply = (e) => {
    e.preventDefault();
    if (!detail || !replyText.trim()) return;
    axios
      .post(
        `${API_BASE}/staff/tickets/${detail.ticket.id}/reply`,
        { body: replyText.trim(), isInternal },
        { headers: headers() }
      )
      .then(() => {
        Notiflix.Notify.success(isInternal ? "Internal note saved" : "Reply sent + member emailed");
        openTicket(detail.ticket.id);
        loadStats();
      })
      .catch(() => Notiflix.Notify.failure("Reply failed"));
    setReplyText("");
  };

  const updateTicket = (patch) => {
    if (!detail) return;
    axios
      .post(
        `${API_BASE}/staff/tickets/${detail.ticket.id}/update`,
        patch,
        { headers: headers() }
      )
      .then(() => {
        openTicket(detail.ticket.id);
        loadTickets();
        loadStats();
        Notiflix.Notify.success("Ticket updated");
      })
      .catch(() => Notiflix.Notify.failure("Update failed"));
  };

  const addStaff = (e) => {
    e.preventDefault();
    axios
      .post(`${API_BASE}/admin/staff`, { email: staffEmail, role: staffRole }, { headers: headers() })
      .then(() => {
        Notiflix.Notify.success(`${staffRole} access granted + email sent`);
        setStaffEmail("");
        loadStaff();
      })
      .catch((err) =>
        Notiflix.Notify.failure(err.response?.data?.message || "Failed to add staff")
      );
  };

  const removeStaff = (memberId) => {
    axios
      .post(`${API_BASE}/admin/staff/remove`, { memberId }, { headers: headers() })
      .then(() => {
        Notiflix.Notify.success("Staff access removed");
        loadStaff();
      })
      .catch(() => Notiflix.Notify.failure("Remove failed"));
  };

  if (!role) {
    return (
      <div>
        <NavBar />
        <div className="flex justify-center items-center h-screen">
          <p className="text-xl font-semibold text-gray-500">Checking staff access…</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <NavBar />
      {/* STAFF WORKSPACE — distinctly different look from the member view */}
      <div
        className="w-full py-10"
        style={{ background: "linear-gradient(160deg, #0f172a, #1e3a8a)", minHeight: "calc(100vh - 180px)" }}
      >
        <div className="mx-4 max-w-7xl mx-auto">
          {/* workspace header */}
          <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-extrabold text-white flex items-center gap-2">
                🛡️ Staff Workspace
              </h1>
              <p className="text-teal-300 text-sm font-semibold">
                ZelTechnology Helpdesk · signed in as{" "}
                <b className="text-white">{role.toUpperCase()}</b>
              </p>
            </div>
            {role === "admin" && (
              <div className="flex gap-2">
                <button
                  onClick={() => setTab("board")}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition ${
                    tab === "board" ? "bg-teal-400 text-blue-900" : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  Ticket Board
                </button>
                <button
                  onClick={() => setTab("team")}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition ${
                    tab === "team" ? "bg-teal-400 text-blue-900" : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  Team
                </button>
              </div>
            )}
          </div>

          {/* stats strip */}
          {stats && tab === "board" && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              {Object.entries(stats).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setFilters({ ...filters, status: filters.status === k ? "" : k })}
                  className={`bg-white/10 hover:bg-white/20 rounded-xl p-4 text-center transition ${
                    filters.status === k ? "ring-2 ring-teal-400" : ""
                  }`}
                >
                  <p className="text-2xl font-extrabold text-white">{v}</p>
                  <p className="text-xs text-teal-200">{statusBadge[k]?.label || k}</p>
                </button>
              ))}
            </div>
          )}

          {tab === "board" && (
            <>
              {/* filters */}
              <div className="flex flex-wrap gap-3 mb-5">
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20"
                  aria-label="Filter by category"
                >
                  <option value="">All categories</option>
                  <option value="general">General</option>
                  <option value="remote">🖥️ Remote</option>
                  <option value="hardware">🎁 Hardware</option>
                  <option value="digital">📈 Growth</option>
                  <option value="lamna">🦈 LAMNA</option>
                </select>
                <select
                  value={filters.assigned}
                  onChange={(e) => setFilters({ ...filters, assigned: e.target.value })}
                  className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20"
                  aria-label="Filter by assignment"
                >
                  <option value="">All tickets</option>
                  <option value="me">Assigned to me</option>
                  <option value="unassigned">Unassigned</option>
                </select>
                <input
                  type="text"
                  placeholder="Ticket # search…"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 placeholder-gray-400 w-40"
                  aria-label="Search ticket number"
                />
                {(filters.status || filters.category || filters.assigned || filters.search) && (
                  <button
                    onClick={() => setFilters({ status: "", category: "", assigned: "", search: "" })}
                    className="px-3 py-2 text-sm text-teal-300 hover:text-teal-200 font-bold"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {/* ticket table */}
              <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/10">
                {tickets === null && (
                  <p className="text-center text-gray-400 py-10">Loading tickets…</p>
                )}
                {tickets && tickets.length === 0 && (
                  <p className="text-center text-gray-400 py-10">No tickets match the filters.</p>
                )}
                {tickets && tickets.length > 0 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-teal-200 text-xs uppercase tracking-wide bg-white/5">
                        <th className="px-4 py-3">#</th>
                        <th className="px-4 py-3">Subject</th>
                        <th className="px-4 py-3 hidden md:table-cell">Member</th>
                        <th className="px-4 py-3 hidden md:table-cell">Category</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 hidden lg:table-cell">Priority</th>
                        <th className="px-4 py-3 hidden lg:table-cell">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((t) => (
                        <tr
                          key={t.id}
                          onClick={() => openTicket(t.id)}
                          className="border-t border-white/10 hover:bg-white/10 cursor-pointer"
                        >
                          <td className="px-4 py-3 text-teal-300 font-bold">{t.id}</td>
                          <td className="px-4 py-3 text-white font-semibold">
                            {t.subject}
                            <span className="ml-2 text-xs text-gray-400">
                              ({t.message_count})
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-300 hidden md:table-cell">
                            {t.member_name}
                            <span className="text-gray-500 hidden xl:inline"> · {t.member_email}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-300 hidden md:table-cell">
                            {categoryLabel[t.category] || t.category}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusBadge[t.status]?.cls}`}>
                              {statusBadge[t.status]?.label || t.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            {t.priority === "high" ? (
                              <span className="text-red-400 font-bold">HIGH</span>
                            ) : (
                              <span className="text-gray-400">{t.priority}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-400 hidden lg:table-cell">
                            {fmtDate(t.updated_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* team management (admin only) */}
          {tab === "team" && role === "admin" && (
            <div className="grid gap-6 md:grid-cols-2">
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h2 className="text-xl font-extrabold text-white mb-4">👥 Current Team</h2>
                {staffList && staffList.length > 0 ? (
                  <div className="space-y-3">
                    {staffList.map((s) => (
                      <div key={s.member_id} className="flex justify-between items-center bg-white/10 rounded-xl p-3">
                        <div>
                          <p className="text-white font-bold text-sm">{s.username}</p>
                          <p className="text-gray-400 text-xs">{s.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 rounded-full text-xs font-bold bg-teal-400 text-blue-900">
                            {s.role}
                          </span>
                          <button
                            onClick={() => removeStaff(s.member_id)}
                            className="text-red-400 hover:text-red-300 text-xs font-bold"
                            aria-label={`Remove ${s.username}`}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No staff yet.</p>
                )}
              </div>
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h2 className="text-xl font-extrabold text-white mb-2">➕ Grant Access</h2>
                <p className="text-gray-400 text-xs mb-4">
                  Add any <b>@zeltechnology.org member account</b> as staff. They'll
                  get an email and see the Staff Workspace on next login.
                </p>
                <form onSubmit={addStaff} className="flex flex-col gap-3">
                  <input
                    type="email"
                    placeholder="employee@zeltechnology.org"
                    value={staffEmail}
                    onChange={(e) => setStaffEmail(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 placeholder-gray-400"
                    required
                  />
                  <select
                    value={staffRole}
                    onChange={(e) => setStaffRole(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20"
                    aria-label="Staff role"
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-teal-400 text-blue-900 text-sm font-bold hover:bg-teal-300"
                  >
                    Grant Access + Email
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ticket detail modal */}
      {detail && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setDetail(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-3xl my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-extrabold text-blue-900">
                    #{detail.ticket.id} · {detail.ticket.subject}
                  </h2>
                  <p className="text-gray-500 text-sm">
                    From: <b>{detail.ticket.member_name}</b> ({detail.ticket.member_email})
                    · {categoryLabel[detail.ticket.category] || detail.ticket.category}
                  </p>
                </div>
                <button
                  onClick={() => setDetail(null)}
                  className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* controls */}
              <div className="flex flex-wrap gap-3 mb-5">
                <select
                  value={detail.ticket.status}
                  onChange={(e) => updateTicket({ status: e.target.value })}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-bold"
                  aria-label="Status"
                >
                  {Object.entries(statusBadge).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <select
                  value={detail.ticket.priority}
                  onChange={(e) => updateTicket({ priority: e.target.value })}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                  aria-label="Priority"
                >
                  <option value="low">Priority: Low</option>
                  <option value="normal">Priority: Normal</option>
                  <option value="high">Priority: High</option>
                </select>
                {role !== "volunteer" && (
                  <select
                    value={detail.ticket.assigned_to || ""}
                    onChange={(e) =>
                      updateTicket({ assignedTo: e.target.value ? Number(e.target.value) : null })
                    }
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                    aria-label="Assigned technician"
                  >
                    <option value="">Unassigned</option>
                    {staffList?.map((s) => (
                      <option key={s.member_id} value={s.member_id}>
                        {s.username} ({s.role})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* remote session log (for remote-category tickets) */}
              {detail.ticket.category === "remote" && (
                <div className="mb-5 bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <h3 className="font-bold text-blue-900 text-sm mb-2">
                    🏠 ZelConnect Remote record
                  </h3>
                  {detail.ticket.session_date && (
                    <p className="text-xs text-gray-600 mb-2">
                      Last logged: {new Date(detail.ticket.session_date).toLocaleString()} ·
                      by {detail.ticket.session_tech || "—"}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={sessionLog}
                      onChange={(e) => setSessionLog(e.target.value)}
                      placeholder="Session summary — e.g. 'Removed 3 scams, installed updates, 35 min'"
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm"
                      maxLength={200}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!sessionLog.trim()) return;
                        updateTicket({ remoteSession: sessionLog.trim() });
                        setSessionLog("");
                      }}
                      className="px-4 py-2 rounded-lg bg-teal-400 text-blue-900 text-sm font-bold hover:bg-teal-300 whitespace-nowrap"
                    >
                      Log Session
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Records: who (you), when (now), what (summary). Per the
                    runbook — never include screen contents or credentials.
                  </p>
                </div>
              )}

              {/* messages */}
              <div className="space-y-3 mb-5 max-h-96 overflow-y-auto">
                {detail.messages.map((m, i) => (
                  <div
                    key={i}
                    className={`rounded-xl p-3 text-sm ${
                      m.is_internal_note
                        ? "bg-amber-50 border-l-4 border-amber-400"
                        : m.author_name === detail.ticket.member_name
                        ? "bg-blue-50 ml-6"
                        : "bg-gray-50 mr-6"
                    }`}
                  >
                    <p className="text-xs text-gray-400 mb-1">
                      {m.author_name} · {new Date(m.created_at).toLocaleString()}
                      {m.is_internal_note && (
                        <span className="ml-2 text-amber-600 font-bold">🔒 internal note</span>
                      )}
                    </p>
                    <p className="text-gray-700 whitespace-pre-wrap">{m.body}</p>
                  </div>
                ))}
              </div>

              {/* reply */}
              <form onSubmit={sendReply} className="border-t pt-4">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write your reply…"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                  rows={4}
                  maxLength={5000}
                  required
                />
                <div className="flex items-center justify-between mt-2">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      disabled={role === "volunteer"}
                    />
                    🔒 Internal note (member never sees or gets emailed)
                    {role === "volunteer" && (
                      <span className="text-xs text-gray-400">— staff only</span>
                    )}
                  </label>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-lg bg-blue-900 text-white text-sm font-bold hover:bg-blue-800"
                  >
                    {isInternal ? "Save Note" : "Send Reply + Email Member"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default StaffWorkspace;
